import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateEmail,
  updatePassword,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  UserCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { auth, db } from './firebase';

export interface UserData {
  // Informatica Credentials (up to 3 instances)
  infaUsername1?: string;
  infaPassword1?: string;
  infaUrl1?: string;
  infaUsername2?: string;
  infaPassword2?: string;
  infaUrl2?: string;
  infaUsername3?: string;
  infaPassword3?: string;
  infaUrl3?: string;
  
  // GitHub Credentials
  gitUsername?: string;
  gitPat?: string;
  
  // GitHub Repositories (up to 4 repositories)
  gitRepo1?: string;
  gitRepo1Branch?: string;
  gitRepo2?: string;
  gitRepo2Branch?: string;
  gitRepo3?: string;
  gitRepo3Branch?: string;
  gitRepo4?: string;
  gitRepo4Branch?: string;
  
  // Confluence Credentials
  confluenceUsername?: string;
  confluenceApiToken?: string;
  confluenceBaseUrl?: string;
  confluenceCloudIdAri?: string;
  confluenceDefaultSpaces?: string; // Comma-separated spaces
  
  // Metadata
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  preferences?: {
    theme?: 'light' | 'dark';
    notifications?: boolean;
    autoSave?: boolean;
  };
}

export class AuthService {
  // Sign up with email and password
  static async signUp(email: string, password: string): Promise<UserCredential> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create initial user data in Firestore
      const initialUserData: UserData = {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        preferences: {
          theme: 'light',
          notifications: true,
          autoSave: true
        }
      };
      
      await this.saveUserData(userCredential.user.uid, initialUserData);
      await sendEmailVerification(userCredential.user);
      
      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  // Sign in with email and password
  static async signIn(email: string, password: string): Promise<UserCredential> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login timestamp
      await this.updateLastLogin(userCredential.user.uid);
      
      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  // Sign out
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      throw error;
    }
  }

  // Send email verification
  static async sendEmailVerification(): Promise<void> {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      }
    } catch (error) {
      throw error;
    }
  }

  // Send password reset email
  static async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  }

  // Update email
  static async updateEmail(newEmail: string): Promise<void> {
    try {
      if (auth.currentUser) {
        await updateEmail(auth.currentUser, newEmail);
      }
    } catch (error) {
      throw error;
    }
  }

  // Update password
  static async updatePassword(newPassword: string): Promise<void> {
    try {
      if (auth.currentUser) {
        await updatePassword(auth.currentUser, newPassword);
      }
    } catch (error) {
      throw error;
    }
  }

  // Enroll in SMS multi-factor authentication
  static async enrollSMSMFA(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<void> {
    try {
      if (auth.currentUser) {
        await multiFactor(auth.currentUser).getSession();
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneNumber, recaptchaVerifier);
        const phoneAuthCredential = PhoneAuthProvider.credential(verificationId, '');
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneAuthCredential);
        await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'SMS');
      }
    } catch (error) {
      throw error;
    }
  }

  // Save user data to Firestore
  static async saveUserData(userId: string, userData: UserData): Promise<void> {
    try {
      const dataWithTimestamp = {
        ...userData,
        updatedAt: new Date().toISOString()
      };
      await setDoc(doc(db, 'users', userId), dataWithTimestamp, { merge: true });
    } catch (error) {
      throw error;
    }
  }

  // Get user data from Firestore
  static async getUserData(userId: string): Promise<UserData | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data() as UserData;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  // Update last login timestamp
  static async updateLastLogin(userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        lastLoginAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  }

  // Update specific field in user data
  static async updateUserField(userId: string, field: keyof UserData, value: any): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        [field]: value,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      throw error;
    }
  }

  // Clear specific credentials (for security)
  static async clearCredentials(userId: string, credentialType: 'infa' | 'git'): Promise<void> {
    try {
      const fieldsToClear: string[] = [];
      
      if (credentialType === 'infa') {
        fieldsToClear.push(
          'infaUsername1', 'infaPassword1', 'infaUrl1',
          'infaUsername2', 'infaPassword2', 'infaUrl2',
          'infaUsername3', 'infaPassword3', 'infaUrl3'
        );
      } else if (credentialType === 'git') {
        fieldsToClear.push(
          'gitUsername', 'gitPat',
          'gitRepo1', 'gitRepo1Branch',
          'gitRepo2', 'gitRepo2Branch',
          'gitRepo3', 'gitRepo3Branch',
          'gitRepo4', 'gitRepo4Branch'
        );
      }

      const updateData: any = {
        updatedAt: new Date().toISOString()
      };
      
      fieldsToClear.forEach(field => {
        updateData[field] = deleteField();
      });

      await updateDoc(doc(db, 'users', userId), updateData);
    } catch (error) {
      throw error;
    }
  }

  // Get current user's data (convenience method)
  static async getCurrentUserData(): Promise<UserData | null> {
    try {
      if (auth.currentUser) {
        return await this.getUserData(auth.currentUser.uid);
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  // Save current user's data (convenience method)
  static async saveCurrentUserData(userData: UserData): Promise<void> {
    try {
      if (auth.currentUser) {
        await this.saveUserData(auth.currentUser.uid, userData);
      } else {
        throw new Error('No authenticated user');
      }
    } catch (error) {
      throw error;
    }
  }

  // Validate user data structure
  static validateUserData(data: any): data is UserData {
    // Basic validation - you can expand this as needed
    return typeof data === 'object' && data !== null;
  }
}
